import { useEffect, useRef } from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';
import recaptchaService from '../../services/RecaptchaService';

/**
 * Hook that initializes and reinitializes reCAPTCHA when GrowthBook feature value changes
 * Handles switching between V2 (checkbox) and V3 (score-based) versions
 * @returns {boolean} - true if using V3 (score-based), false if using V2 (checkbox)
 */
export default function useRecaptchaInit() {
  const useScoreCaptcha = useFeatureValue('score-based-recaptcha', false);
  const previousUseScoreCaptcha = useRef(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    const reinitialize = async () => {
      // Check if version actually changed (skip on first render)
      const versionChanged =
        isInitialized.current &&
        previousUseScoreCaptcha.current !== useScoreCaptcha;

      // If version changed, we need to clean up the old one
      if (versionChanged) {
        // Clean up V2 widget if it exists (when switching from V2 to V3)
        if (!useScoreCaptcha && previousUseScoreCaptcha.current) {
          recaptchaService.cleanupV2Widget();
        }

        // Remove old script if it exists
        const oldScript = document.getElementById('recaptcha-script');
        if (oldScript) {
          oldScript.remove();
        }

        // Clear grecaptcha instance
        if (window.grecaptcha) {
          delete window.grecaptcha;
        }

        // Reset loading promise
        recaptchaService.loadingPromise = null;
      }

      // Update the version in RecaptchaService (will notify subscribers via observer pattern)
      recaptchaService.setVersion(useScoreCaptcha ? 'v3' : 'v2');

      // Load the reCAPTCHA script with the current version
      try {
        await recaptchaService.ensureLoaded();

        // For V2, after loading, the CaptchaCheckBox component will handle rendering
        // For V3, it's ready to use immediately (invisible)
      } catch {
        // Error is already logged in RecaptchaService and notified to subscribers
      }

      // Mark as initialized and save current version
      previousUseScoreCaptcha.current = useScoreCaptcha;
      isInitialized.current = true;
    };

    reinitialize();
  }, [useScoreCaptcha]);

  return useScoreCaptcha;
}
